import { lookupTranslation, normalizeLanguageCode } from './i18n'

export const NORVO_AI_NAME = 'NorvoAi'
export const BBG_PRODUCT_NAME = 'BookBuilderGo'
export const NORVO_AI_LEGAL_NAME = 'NorvoAi'
/** Optional public site — update when official URL is confirmed */
export const NORVO_AI_URL = 'https://norvoai.com'

export type NorvoBrandCopy = {
  footerCopyright: string
  inline: string
  blockTitle: string
  blockBody: string
  settings: string
  aboutLead: string
  legalOwnerLine: string
}

const CURRENT_YEAR = new Date().getFullYear()

const NORVO_BRAND: Record<string, NorvoBrandCopy> = {
  EN: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Owned and built by NorvoAi`,
    inline: 'Owned & built by NorvoAi',
    blockTitle: 'Owned and built by NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) is the author platform. NorvoAi owns, develops, and operates the product — including LITRA!, infrastructure, and ongoing improvements.',
    settings: 'BookBuilderGo · Owned and built by NorvoAi',
    aboutLead:
      'BookBuilderGo is a NorvoAi product. NorvoAi designs, builds, and maintains the platform so authors can go from idea to published book.',
    legalOwnerLine:
      'BookBuilderGo is owned, developed, and operated by NorvoAi (“we”, “us”, “our”).',
  },
  DA: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Ejet og bygget af NorvoAi`,
    inline: 'Ejet og bygget af NorvoAi',
    blockTitle: 'Ejet og bygget af NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) er forfatterplatformen. NorvoAi ejer, udvikler og driver produktet — inkl. LITRA!, infrastruktur og løbende forbedringer.',
    settings: 'BookBuilderGo · Ejet og bygget af NorvoAi',
    aboutLead:
      'BookBuilderGo er et NorvoAi-produkt. NorvoAi designer, bygger og vedligeholder platformen, så forfattere kan gå fra idé til udgivet bog.',
    legalOwnerLine:
      'BookBuilderGo ejes, udvikles og drives af NorvoAi (“vi”, “os”, “vores”).',
  },
  DE: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Eigentum und Entwicklung: NorvoAi`,
    inline: 'Eigentum und Entwicklung: NorvoAi',
    blockTitle: 'Eigentum und Entwicklung: NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) ist die Autorenplattform. NorvoAi besitzt, entwickelt und betreibt das Produkt — einschließlich LITRA!, Infrastruktur und Weiterentwicklung.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo ist ein NorvoAi-Produkt. NorvoAi entwickelt und betreibt die Plattform für Autorinnen und Autoren.',
    legalOwnerLine:
      'BookBuilderGo wird von NorvoAi besessen, entwickelt und betrieben („wir“, „uns“, „unser“).',
  },
  FR: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Propriété et développement : NorvoAi`,
    inline: 'Propriété et développement : NorvoAi',
    blockTitle: 'Propriété et développement : NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) est la plateforme auteur. NorvoAi possède, développe et exploite le produit — y compris LITRA!, l’infrastructure et les évolutions.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo est un produit NorvoAi. NorvoAi conçoit, construit et maintient la plateforme pour les auteurs.',
    legalOwnerLine:
      'BookBuilderGo est détenu, développé et exploité par NorvoAi (« nous », « notre »).',
  },
  ES: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Propiedad y desarrollo: NorvoAi`,
    inline: 'Propiedad y desarrollo: NorvoAi',
    blockTitle: 'Propiedad y desarrollo: NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) es la plataforma para autores. NorvoAi posee, desarrolla y opera el producto, incluido LITRA!, la infraestructura y las mejoras.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo es un producto de NorvoAi. NorvoAi diseña, construye y mantiene la plataforma para autores.',
    legalOwnerLine:
      'BookBuilderGo es propiedad, está desarrollado y operado por NorvoAi («nosotros», «nuestro»).',
  },
  IT: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Proprietà e sviluppo: NorvoAi`,
    inline: 'Proprietà e sviluppo: NorvoAi',
    blockTitle: 'Proprietà e sviluppo: NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) è la piattaforma per autori. NorvoAi possiede, sviluppa e gestisce il prodotto, inclusi LITRA!, infrastruttura e miglioramenti.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo è un prodotto NorvoAi. NorvoAi progetta, costruisce e mantiene la piattaforma per gli autori.',
    legalOwnerLine:
      'BookBuilderGo è di proprietà, sviluppato e gestito da NorvoAi («noi», «nostro»).',
  },
  NL: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Eigendom en ontwikkeling: NorvoAi`,
    inline: 'Eigendom en ontwikkeling: NorvoAi',
    blockTitle: 'Eigendom en ontwikkeling: NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) is het auteursplatform. NorvoAi bezit, ontwikkelt en beheert het product — inclusief LITRA!, infrastructuur en doorontwikkeling.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo is een NorvoAi-product. NorvoAi ontwerpt, bouwt en onderhoudt het platform voor auteurs.',
    legalOwnerLine:
      'BookBuilderGo is eigendom van, ontwikkeld en beheerd door NorvoAi (“wij”, “ons”, “onze”).',
  },
  PL: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Własność i rozwój: NorvoAi`,
    inline: 'Własność i rozwój: NorvoAi',
    blockTitle: 'Własność i rozwój: NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) to platforma dla autorów. NorvoAi jest właścicielem, twórcą i operatorem produktu — w tym LITRA!, infrastruktury i rozwoju.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo to produkt NorvoAi. NorvoAi projektuje, buduje i utrzymuje platformę dla autorów.',
    legalOwnerLine:
      'BookBuilderGo jest własnością, rozwijany i obsługiwany przez NorvoAi („my”, „nasz”).',
  },
  PT: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Propriedade e desenvolvimento: NorvoAi`,
    inline: 'Propriedade e desenvolvimento: NorvoAi',
    blockTitle: 'Propriedade e desenvolvimento: NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) é a plataforma para autores. A NorvoAi possui, desenvolve e opera o produto — incluindo LITRA!, infraestrutura e melhorias.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo é um produto NorvoAi. A NorvoAi concebe, constrói e mantém a plataforma para autores.',
    legalOwnerLine:
      'O BookBuilderGo é propriedade, desenvolvido e operado pela NorvoAi («nós», «nosso»).',
  },
  SE: {
    footerCopyright: `© ${CURRENT_YEAR} BookBuilderGo · Ägs och byggs av NorvoAi`,
    inline: 'Ägs och byggs av NorvoAi',
    blockTitle: 'Ägs och byggs av NorvoAi',
    blockBody:
      'BookBuilderGo (BBG) är författarplattformen. NorvoAi äger, utvecklar och driver produkten — inklusive LITRA!, infrastruktur och förbättringar.',
    settings: 'BookBuilderGo · NorvoAi',
    aboutLead:
      'BookBuilderGo är en NorvoAi-produkt. NorvoAi designar, bygger och underhåller plattformen för författare.',
    legalOwnerLine:
      'BookBuilderGo ägs, utvecklas och drivs av NorvoAi (“vi”, “oss”, “vår”).',
  },
}

export function getNorvoBrandCopy(lang?: string): NorvoBrandCopy {
  const code = normalizeLanguageCode(lang || 'EN')
  const keys = new Set([...Object.keys(NORVO_BRAND.EN), ...Object.keys(NORVO_BRAND[code] ?? {})])
  const out = {} as NorvoBrandCopy
  for (const key of keys) {
    const value = lookupTranslation(NORVO_BRAND, code, key)
    if (typeof value === 'string') (out as Record<string, string>)[key] = value
  }
  return out
}
